import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Download } from 'lucide-react';
import { api } from '@/lib/api';
import type { NewsletterSubscriber } from '@inafana/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';

export function NewsletterList(): React.JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'newsletter'],
    queryFn: () => api<{ subscribers: NewsletterSubscriber[] }>('/admin/newsletter'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/newsletter/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'newsletter'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Newsletter</h1>
          <p className="text-sm text-muted-foreground">
            {data?.subscribers.length ?? 0} subscribers
          </p>
        </div>
        <Button asChild variant="outline">
          <a href="/api/admin/newsletter/export.csv">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </a>
        </Button>
      </header>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Confirmed</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.subscribers.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No subscribers yet.
                </TableCell>
              </TableRow>
            )}
            {data?.subscribers.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.email}</TableCell>
                <TableCell>{s.confirmed ? 'yes' : 'no'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(s.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Remove ${s.email}?`)) remove.mutate(s.id);
                    }}
                    disabled={remove.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
